(ns app.front.view.party.edit
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.dashlets :as dashlets]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.validate :as validate]
            [app.front.view.business.information :as business-info-form]
            [app.front.view.party.common :as common]
            [app.front.view.party.subs :as party-subs]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]))


(def initial-party-form
  {:db/id                           nil
   :user/first-name                 nil
   :user/last-name                  nil
   :user/street-address             nil
   :user/unit-number                nil
   :user/postal-code                nil
   :user/municipality               nil
   :user/province                   nil
   :user/corporation-or-partnership nil
   :user/mobile-phone               nil
   :user/other-phone                nil
   :party/email                     nil
   :party/party-roles               []
   :party/permissions               nil
   :party/user                      nil
   ::permissions-received?          false
   ::initial-permissions            #{}
   ::common/errors                  {}
   ::common/server-errors           nil
   ::common/show-errors?            false})

(rf/reg-sub ::edit-party-open?
  (fn [db [_ id]]
    (get (:edit-party-open db) id)))


(rf/reg-event-db ::toggle-edit-party-modal
  (fn [db [_ id]]
    (-> db
        (update-in [:edit-party-open id] not))))

(rc/reg-chain ::init
  (fn [{:keys [db]} [_ party]]
    {:comm/command {:command :party/fetch-permissions
                    :data    {:party-id (:db/id party)}}
     :db           (-> db
                       (assoc ::common/document (merge initial-party-form party))
                       ;; get party-role ids directly from entity-db, because the party-role entities
                       ;; themselves may not have been fetched yet.
                       (assoc-in [::common/document :party/party-roles]
                                 (get-in db [::entities/entity-db (:db/id party) :party/party-roles]))
                       (assoc-in [::common/document ::common/show-errors?] true))})
  (fn [{:keys [db]} [_ party _ response]]
    (when (get (:edit-party-open db) (:db/id party))
      {:db (-> db
               (dissoc ::common/deferred-edit)
               (assoc-in [::common/document :party/permissions] response)
               (assoc-in [::common/document ::permissions-received?] true)
               (assoc-in [::common/document ::initial-permissions] response))})))

(rf/reg-sub ::permissions-received?
  :<- [::common/document]
  :-> ::permissions-received?)

(rf/reg-sub ::initial-permissions
  :<- [::common/document]
  :-> ::initial-permissions)

(rf/reg-event-fx ::save
  (fn [{:keys [db]} [_ path value party-id target-id extra-signal-params]]
    (when-not (keyword? (validate/validate-field path (::common/document db) common/form-model))
      {:dispatch [::process/signal
                  target-id
                  [:process.signal/edit-party
                   (merge {:field-path path
                           :new-value  value
                           :party-id   party-id}
                          extra-signal-params)]
                  {:track-save? true}]})))

(def party-role-signals
  {:add    :process.signal/add-party-role
   :remove :process.signal/remove-party-role})

(rf/reg-event-fx ::edit
  (fn [{:keys [db]} [_ path role-id party-id op target-id]]
    (when (not (validate/validate-field path (::common/document db) common/form-model))
      {:dispatch [::process/signal
                  target-id
                  [(party-role-signals op)
                   {:role-id  role-id
                    :party-id party-id}]
                  {:track-save? true}]})))


(rf/reg-event-fx ::edit-party-permissions
  (fn [_ [_ permission party-id op target-id]]
    {:dispatch [::process/signal
                target-id
                [:process.signal/edit-party-permissions
                 {:party-id   party-id
                  :permission permission
                  :operation  op}]]}))

;; Remove party

(rf/reg-sub ::delete-confirm-open?
  (fn [db [_ id]]
    (get (:remove-party-confirm-open db) id)))


(rf/reg-event-db ::toggle-delete-confirm-modal
  (fn [db [_ id]]
    (-> db
        (update-in [:remove-party-confirm-open id] not)
        (assoc ::remove-from-workspace? false))))


(rf/reg-sub ::remove-from-workspace?
  :-> (comp boolean ::remove-from-workspace?))


(rf/reg-event-db ::set-remove-from-workspace
  (fn [db [_ enable]] (assoc db ::remove-from-workspace? enable)))


(rf/reg-event-fx ::remove-party.success
  (fn [{:keys [db]} [_ party-id removed-self? user-id app-id on-success status _]]
    (when (= 200 status)
      (merge {:dispatch-n (cond-> [[::toggle-delete-confirm-modal party-id]]
                            removed-self? (conj [::dashlets/delete-item
                                                 (str user-id "-my-applications")
                                                 app-id])
                            on-success (conj on-success))
              :db         db}
             (when removed-self?
               {:route/push :dashboard})))))


(rf/reg-event-fx ::remove-party
  (fn [{{::keys [remove-from-workspace?]} :db} [_ party-id target-id remove-self? user-id opts]]
    {:dispatch [::process/signal
                target-id
                (if remove-from-workspace?
                  [:process.signal/remove-workspace-party {:party-id party-id}]
                  [:process.signal/remove-party {:party-id party-id}])
                {:on-done     [::remove-party.success party-id remove-self? user-id target-id (:on-success opts)]
                 :notify      {:success :party/remove-success}
                 :track-save? true}]}))

(defn remove-party-modal [tr party-id target-id remove-self? user-id party-has-user?
                          & [{:keys [on-success]}]]
  (cc/modal {:open                  (or (<sub [::delete-confirm-open? party-id]) false)
             :primary-button-text   (tr [:party/remove])
             :secondary-button-text (tr [:party/cancel-button])
             :modal-heading         (tr [:party/remove-title])
             :on-request-close      #(>evt [::toggle-delete-confirm-modal party-id])
             :on-request-submit     #(>evt [::remove-party party-id target-id remove-self? user-id
                                            {:on-success on-success}])
             :on-secondary-submit   #(>evt [::toggle-delete-confirm-modal party-id])
             :danger                true}
            [:div.flex.flex-col
             [:p.bx--modal-content__text.mb-6
              (tr [:party/confirm])]
             (when (<sub [::process/signal-permitted?
                          target-id
                          [:process.signal/remove-workspace-party {:party-id party-id}]])
               (cc/checkbox {:id         "remove-workspace-party-checkbox"
                             :label-text (tr (if (ff/enabled? :feature-flag.id/allow-party-removal)
                                               (if party-has-user?
                                                 [:party/remove-from-workspace-party-has-user]
                                                 [:party/remove-from-workspace-party-has-no-user])
                                               [:party/remove-from-workspace]))
                             :checked    (<sub [::remove-from-workspace?])
                             :on-change  (fn [check] (>evt [::set-remove-from-workspace check]))}))]))

(defn reject-remove? [target-id party-id role-id]
  (not (<sub [::process/signal-permitted?
              target-id
              [:process.signal/remove-party-role {:party-id party-id
                                                  :role-id  role-id}]])))

(defn party-timeline [{:keys [tr party]}]
  (let [invite      (->> party
                         :invite/_party
                         first)
        format-date (<sub [::date-util/date-fn])
        create-date (when-let [created (:entity/created party)]
                      (format-date created))
        invite-date (when-let [invite-date (:invite/date invite)]
                      (format-date invite-date))]
    [:ul.text-xs
     (if (and (some? create-date) (= create-date invite-date))
       [:li (tr [:party/added-and-invited-on]
                [create-date])]
       [:<>
        (when create-date
          [:li (tr [:party/added-on]
                   [create-date])])
        (when invite-date
          [:li (tr [:party/invited-on]
                   [invite-date])])])]))

(defn available-extra-permissions [workspace party-has-user? current-phase-type]
  ;; CE does not allow any extra permissions for parties
  (let [has-previous-phases? (->> (workspace-model/previous-phase-entity-types workspace current-phase-type)
                                  not-empty
                                  boolean)]
    (when (and party-has-user? (-> workspace :workspace/permit-type (not= :permit-type/CE)))
      (cond-> [:party/modify-application]
        has-previous-phases? (conj :party/view-other-phases)
        (-> workspace :entity/parent) (conj :party/add-application)))))

(defn- changed-permissions [selected-permissions existing-permissions]
  (let [added-permissions   (->> (set/difference selected-permissions existing-permissions)
                                 vec
                                 (map #(conj {:permission % :operation :add})))
        removed-permissions (->> (set/difference existing-permissions selected-permissions)
                                 vec
                                 (map #(conj {:permission % :operation :remove})))]
    (concat added-permissions removed-permissions)))


(defn- close-and-save-extra-permissions [party-id target-id selected-permissions existing-permissions extra-perms]
  (when (not-empty extra-perms)
    (let [changed-permissions (changed-permissions selected-permissions existing-permissions)]
      (run! (fn [extra-permission]
              (let [operation  (:operation extra-permission)
                    permission (:permission extra-permission)]
                (>evt [::edit-party-permissions
                       permission
                       party-id
                       operation
                       target-id]))) changed-permissions)))
  (>evt [::toggle-edit-party-modal party-id]))

(defn edit-party-modal [tr party target]
  (let [validator            #(validate/validate-field % (<sub [::common/document]) common/form-model)
        party-id             (:db/id party)
        existing-permissions (<sub [::common/document [::initial-permissions]])
        user-id              (<sub [:session/user-id])
        users-party?         (= user-id (:db/id (:party/user party)))
        target-id            (:db/id target)
        remove-self?         (<sub [::party-subs/only-party-of-user? target-id party-id])
        ;; when adding email to existing manual party, show some more controls
        party-has-user?      (some? (:party/user party))
        open?                (<sub [::edit-party-open? (:db/id party)])
        target-id            (:db/id target)
        selected-permissions (<sub [::common/document [:party/permissions]])
        current-phase        (-> party :entity/parent :entity/type)
        extra-perms          (-> target
                                 :entity/parent
                                 (available-extra-permissions
                                   (or party-has-user?
                                       (some? (:party/business party)))
                                   current-phase))]
    [:div
     (when open?
       (cc/modal
         {:open          open?
          :passive-modal true
          :aria-label    (tr [:party/edit])
          :class-name    "party-modal"
          :data-cy       "edit-party-modal"}
         [:div.flex.flex-col
          [:div.bx--row
           [:div.bx--col-xs-12.bx--col-md-3.bx--col-lg-4.bx--col-xl-5.bx--col-xxl-6.mb-6
            [:h3.mb-2 (tr [:party/edit])]
            [party-timeline {:tr tr :party party}]]
           [:div.bx--col-xs-12.bx--col-md-6.bx--col-lg-5.bx--col-xl-4.bx--col-xxl-3.mb-6.text-right
            (cc/button
              {:kind             :danger
               :icon             i/trash
               :disabled         (not (<sub [::process/signal-permitted?
                                             target-id
                                             [:process.signal/remove-party {:party-id (:db/id party)}]]))
               :icon-description (tr [:party/remove-party])
               :class-name       "mt-1"
               :on-click         #(>evt [::toggle-delete-confirm-modal party-id])}
              (tr [:party/remove-party]))]
           [:div.bx--col-xs-12.bx--col-md-3..bx--col-lg-3.bx--col-xl-3.bx--col-xxl-3.mb-6.text-right
            (cc/button
              {:kind             :tertiary
               :icon             i/remove
               :icon-description (tr [:party/close])
               :class-name       "mt-1"
               :on-click         #(close-and-save-extra-permissions party-id
                                                                    target-id
                                                                    selected-permissions
                                                                    existing-permissions
                                                                    extra-perms)}
              (if (seq (changed-permissions selected-permissions existing-permissions))
                (tr [:party/close-and-save])
                (tr [:party/close])))]]]
         [:<>
          (if-let [business (:party/business party)]
            [:div
             [:div (tr [:party-modal/edit-business-info])]
             [business-info-form/read-only-form tr business]]
            [:form.flex.flex-col.w-full
             [:fieldset
              [common/common-editor-fields-v2
               (util/mmap {:disabled?      (fn [field]
                                             (not (<sub [::process/signal-permitted?
                                                         target-id
                                                         [:process.signal/edit-party (-> {:party-id party-id}
                                                                                         (p/assoc-if
                                                                                           :field-path field))]])))
                           :on-save-needed (fn [field value]
                                             (>evt [::save field value party-id target-id]))
                           :show-email?    true}
                          tr target-id validator)]]])
          [:hr]
          [:div.bx--row
           [:div.bx--col-xs-12.bx--col-md-6
            [common/role-selector (util/mmap {:on-toggle           (fn [operation role]
                                                                     (>evt [::edit :party/party-roles
                                                                            (:db/id role)
                                                                            party-id
                                                                            operation
                                                                            target-id]))
                                              :reject-role-remove? (partial reject-remove? target-id party-id)
                                              :legend-tr-key       (if (:party/business party)
                                                                     :party-modal/business-role-legend
                                                                     :party/role-legend)}
                                             tr validator target-id)]]
           (when (seq extra-perms)
             [:div.bx--col-xs-12.bx--col-md-6
              (if (<sub [::permissions-received?])
                [common/additional-permissions (util/mmap {:disabled?      users-party?
                                                           :permissions    extra-perms
                                                           :editing-party? true
                                                           :party          party}
                                                          tr validator target)]
                (cc/loading-spinner {:with-overlay false}))])]]))
     [remove-party-modal tr party-id target-id remove-self? user-id party-has-user?]]))
